import { reviewThemeNames } from "@domains/reviews/shared/reviewThemes.ts";
import { goalsEn } from "@domains/goals/client/i18n/en.ts";
import { goodVibesEn, goodVibeCardEn, goodVibeDetailDialogEn } from "@domains/good-vibes/client/i18n/en.ts";

export const en = {
    ...goalsEn,
    ...goodVibesEn,
    Navigation: {
        team: {
            label: "Team",
            description: "Team views and reports",
            main: {
                label: "Team",
                overview: { label: "Overview" },
                engagement: { label: "Engagement" },
                meetings: { label: "Meetings" },
                "good-vibes": { label: "Good Vibes" },
                feedback: { label: "Feedback" },
                reviews: { label: "Reviews" },
                goals: { label: "Goals" }
            }
        },
        "team-reviews-detail": { label: "Review" },
        "team-reviews-write": { label: "Write review" },
        "team-reviews-share": { label: "Share review" },
        "team-goals-detail": { label: "Goal" },
        "team-goals-edit": { label: "Edit goal" },
        "team-engagement-detail": { label: "Metric" }
    },
    Errors: {
        retry: "Try again",
        retrying: "Retrying…",
        reassurance: "Please try again later.",
        refreshBanner: "Couldn't refresh — showing the last results."
    },
    TeamOverviewPage: {
        error: "We couldn't load the team overview right now.",
        widgetError: {
            engagement: "We couldn't load Engagement right now.",
            goals: "We couldn't load Goals right now.",
            feedback: "We couldn't load Feedback right now.",
            reviews: "We couldn't load Performance reviews right now.",
            goodVibes: "We couldn't load Good Vibes right now."
        },
        segmentUnsupported: {
            engagement: "Engagement doesn't support advanced filtering yet.",
            feedback: "Feedback doesn't support advanced filtering yet.",
            goals: "This summary doesn't reflect the applied filter yet. Open the Goals tab to see it there."
        },
        feedbackAudienceRefusal: {
            segmentNotVisible: "You no longer have access to this segment, so feedback can't be scoped to it.",
            handPickedSegment: "Feedback can only be scoped to rule-based segments, and this one is hand-picked.",
            brokenRules: "Some of this segment's rules aren't supported, so feedback can't be scoped to it.",
            unsupportedCapability: "Feedback can't be scoped to a segment that matches any rule."
        },
        liveRegion: {
            loading: "Loading team overview"
        }
    },
    TeamGoalsWidget: {
        title: "Goals",
        viewAll: "View all",
        viewAllAriaLabel: "View all goals",
        viewStatusGoals: "View {{status}} goals",
        averageProgress: "Average progress",
        onTrack: "On track",
        atRisk: "At risk",
        offTrack: "Off track",
        noStatus: "No status",
        totalCount_one: "{{count}} goal",
        totalCount_other: "{{count}} goals"
    },
    TeamFeedbackWidget: {
        title: "Feedback",
        viewAll: "View all",
        viewAllAriaLabel: "View all feedback",
        unreadMessages_one: "unread message",
        unreadMessages_other: "unread messages",
        unrepliedMessages_one: "unreplied message",
        unrepliedMessages_other: "unreplied messages",
        period: "Last 30 days"
    },
    EngagementRating: {
        critical: "Critical",
        veryLow: "Very low",
        low: "Low",
        good: "Good",
        veryGood: "Very good",
        great: "Great",
        amazing: "Amazing"
    },
    TeamEngagementWidget: {
        title: "Engagement",
        viewAll: "View all",
        viewAllAriaLabel: "View all engagement",
        score: "Score",
        enps: "eNPS",
        band: {
            "needs-work": "Needs work",
            neutral: "Neutral",
            good: "Good",
            excellent: "Excellent"
        },
        delta_one: "{{sign}}{{value}} pt",
        delta_other: "{{sign}}{{value}} pts",
        footer_one: "Last 30 days · Based on {{rate}}% participation · {{count}} of {{invited}} responded",
        footer_other: "Last 30 days · Based on {{rate}}% participation · {{count}} of {{invited}} responded",
        scoreAriaLabel: "Score {{value}} out of 10",
        enpsAriaLabel: "eNPS {{value}} on a scale from -100 to +100",
        noData: "No data"
    },
    TeamPerformanceReviewsWidget: {
        title: "Performance reviews",
        viewAll: "All reviews",
        viewAllAriaLabel: "All reviews",
        averageScore: "Average score",
        scoreScale: "/ {{scale}}",
        scoreDistribution: "Score distribution",
        noRatings: "No ratings available for this cycle",
        positiveSentiments: "Positive sentiments",
        positiveSentimentsEmpty: "No positive themes surfaced.",
        negativeSentiments: "Negative sentiments",
        negativeSentimentsEmpty: "No concerns surfaced.",
        distributionRow_one: "{{count}} review scored {{score}} out of {{scale}}",
        distributionRow_other: "{{count}} reviews scored {{score}} out of {{scale}}",
        footer_one: "{{count}} person reviewed · Based on each person's latest review",
        footer_other: "{{count}} people reviewed · Based on each person's latest review",
        themes: reviewThemeNames("en-US")
    },
    TeamOrgChartWidget: {
        peopleCount_one: "{{count}} person",
        peopleCount_other: "{{count}} people",
        subtext: "Led by <strong>you</strong>. Team metrics exclude the lead.",
        viewInOrgChart: "View in org chart",
        orgChartModal: {
            eyebrow: "Heads up",
            title: "One quick detour",
            body: "The full org chart isn't available in the new app yet. We'll take you to Pingboard to see it.",
            cancel: "Cancel",
            continue: "Continue in Pingboard"
        },
        youBadge: "You",
        youSuffix: "{{name}} (you)",
        unknownMember: "Unknown member",
        viewMoreInOrgChart_one: "View {{count}} more in the org chart",
        viewMoreInOrgChart_other: "View {{count}} more in the org chart",
        tooltipMore: "and {{count}} more",
        empty: {
            title: "You're not on the org chart yet",
            description: "Once you're placed on the org chart, your team will show up here."
        },
        error: "We couldn't load the org chart right now.",
        liveRegion: {
            loading: "Loading org chart"
        }
    },
    TeamCompanyOrgChartWidget: {
        label: "Org chart",
        viewInOrgChart: "View in org chart",
        treeLabel: "Company org chart",
        unknownMember: "Unknown member",
        youBadge: "You",
        youSuffix: "{{name}} (you)",
        viewMore: "View {{count}} more in the org chart",
        tooltipMore: "and {{count}} more",
        liveRegion: {
            loading: "Loading the org chart"
        },
        empty: {
            title: "No one's in the org chart yet."
        },
        error: {
            title: "Couldn't load the org chart.",
            description: "Please try again later.",
            retry: "Try again",
            retrying: "Retrying"
        },
        orgChartModal: {
            eyebrow: "Heads up",
            title: "One quick detour",
            body: "The full org chart isn't available in the new app yet. We'll take you to Pingboard to see it.",
            cancel: "Cancel",
            continue: "Continue in Pingboard"
        }
    },
    TeamReportingLineOrgChartWidget: {
        peopleCount_one: "{{count}} person",
        peopleCount_other: "{{count}} people",
        subtext: "Led by <strong>you</strong>. Team metrics exclude the lead.",
        viewInOrgChart: "View in org chart",
        treeLabel: "All reports",
        youBadge: "You",
        youSuffix: "{{name}} (you)",
        unknownMember: "Unknown member",
        viewMore: "View {{count}} more in the org chart",
        tooltipMore: "and {{count}} more",
        liveRegion: {
            loading: "Loading the org chart"
        },
        empty: {
            title: "You're not on the org chart yet",
            description: "Once you're placed on the org chart, your team will show up here."
        },
        error: "Couldn't load the org chart.",
        orgChartModal: {
            eyebrow: "Heads up",
            title: "One quick detour",
            body: "The full org chart isn't available in the new app yet. We'll take you to Pingboard to see it.",
            cancel: "Cancel",
            continue: "Continue in Pingboard"
        }
    },
    TeamEngagementPage: {
        title: "Engagement",
        sectionError: {
            scores: "We couldn't load the engagement scores right now.",
            enps: "We couldn't load eNPS right now."
        },
        segmentUnsupported: {
            title: "Advanced filtering isn't supported here yet",
            description: "Engagement doesn't support advanced filtering yet. Switch to My team or Everyone to see this tab."
        },
        heatmapLink: {
            title: "Heatmap",
            description: "Compare engagement across departments on the full report.",
            viewHeatmap: "View heatmap"
        },
        liveRegion: {
            loading: "Loading engagement data"
        },
        timeFrame: {
            ariaLabel: "Time frame",
            clearAriaLabel: "Clear time frame filter",
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year",
            allTime: "All time",
            relativeLabel: {
                "7d": "7 days",
                "30d": "30 days",
                "3m": "3 months",
                "6m": "6 months",
                quarter: "this quarter",
                year: "this year",
                allTime: "all time"
            }
        }
    },
    OverallScoreCard: {
        title: "Latest engagement score",
        subtitle: "Latest scores are always calculated based on responses within a 90-day window following the timeframe selected.",
        delta_one: "{{sign}}{{value}} pt vs prior {{period}}",
        delta_other: "{{sign}}{{value}} pts vs prior {{period}}",
        deltaZero: "No change vs prior {{period}}",
        noData: "Not available",
        notEnoughResponses: "Not enough responses",
        notEnoughResponsesAnonymity: "Fewer than 3 responses were collected during this period. Results are hidden to protect anonymity.",
        scoreAriaLabel: "Engagement score",
        scoreNotAvailableAriaLabel: "Engagement score not available: not enough responses",
        outOf: "out of {{max}}",
        valueLabel: "{{score}} out of {{max}}",
        yoy: {
            label: "Your team, same time last year",
            labelOrganization: "Company, same time last year",
            availableFrom: "Available {{date}}",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        enps: {
            label: "Employee NPS",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        participation: {
            label: "Participation rate",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        sparkline: {
            yourTeam: "Your team",
            company: "Company"
        }
    },
    MetricsAndDrivers: {
        title: "Metrics and drivers",
        description: "The metrics that moved the most this period.",
        notEnoughResponses: "Not enough responses",
        notEnoughResponsesAnonymity: "Fewer than 3 responses were collected during this period. Results are hidden to protect anonymity.",
        topDecreases: "Top decreases",
        topGains: "Top gains",
        showAll: "Show all metrics",
        hideAll: "Hide all metrics",
        delta_one: "{{sign}}{{value}} pt",
        delta_other: "{{sign}}{{value}} pts",
        moverLinkAriaLabel: "View {{label}} question results",
        noPriorPeriodTitle: "Not enough history yet",
        noPriorPeriod: "Metric changes will appear once your team has a full period to compare against.",
        noDecreases: "No notable decreases this period.",
        noGains: "No notable gains this period.",
        tooltipLabel: "About {{metric}}",
        table: {
            ariaLabel: "Engagement metrics",
            noDelta: "No comparison available",
            columns: {
                metric: "Metric",
                score: "Score",
                changeOver: "Change over {{period}}",
                details: "Open metric details"
            },
            liveRegion: {
                sortChanged: "Sorted by {{column}} {{direction}}",
                ascending: "ascending",
                descending: "descending",
                columns: {
                    metric: "Metric",
                    score: "Score",
                    change: "Change"
                }
            }
        }
    },
    EnpsSection: {
        title: "Employee Net Promoter Score",
        intro: "Scores reflect the last 90 days of responses, independent of the date range selected above.",
        tooltip: "Employees rate how likely they are to recommend working here on a 0–10 scale. Promoters score 9–10, passives 7–8, detractors 0–6.",
        tooltipLabel: "About eNPS",
        learnMore: "Learn more",
        offBanner: "eNPS surveys are off. Showing the last score from {{date}}.",
        offBannerDateless: "eNPS surveys are off. Showing the last available score.",
        offBannerNoScore: "eNPS surveys are off for your organization.",
        showDistribution: "Show distribution",
        hideDistribution: "Hide distribution",
        notEnoughResponsesAnonymity: "Fewer than 3 responses were collected during this period. Results are hidden to protect anonymity.",
        legend: {
            yourTeam: "Your team",
            company: "Company"
        }
    },
    TeamGoodVibesPage: {
        error: "We couldn't load good vibes right now.",
        listAriaLabel: "Good vibes",
        liveRegion: {
            loading: "Loading Good Vibes",
            loadingMore: "Loading more Good Vibes",
            loadedMore_one: "Loaded {{count}} more Good Vibe",
            loadedMore_other: "Loaded {{count}} more Good Vibes"
        },
        empty: "No Good Vibes match these filters.",
        emptyTimeframe: {
            "7d": "No Good Vibes in the last 7 days.",
            "30d": "No Good Vibes in the last 30 days.",
            "3m": "No Good Vibes in the last 3 months.",
            "6m": "No Good Vibes in the last 6 months.",
            quarter: "No Good Vibes this quarter.",
            year: "No Good Vibes this year."
        },
        emptyAllTime: "No Good Vibes yet.",
        giveModal: {
            eyebrow: "Heads up",
            title: "One quick detour",
            body: "This feature isn't available in the new app yet. We'll take you to Officevibe to complete it. Nothing will be lost.",
            cancel: "Cancel",
            continue: "Continue in Officevibe"
        },
        composeDialog: {
            title: "Send Good Vibes",
            recipientsLabel: "To",
            recipientsPlaceholder: "Add another",
            recipientsSearchPlaceholder: "Search people",
            recipientsSelectedLabel: "Selected recipients",
            recipientsEmpty: "No results",
            recipientsLoading: "Searching…",
            subjectLabel: "Subject",
            subjectPlaceholder: "What did they do?",
            writeOwnLabel: "Write your own, or pick a prompt",
            useTypedLabel: 'Use "{{text}}"',
            customCategoryLabel: "Custom",
            changeSubjectLabel: "Change",
            recipientsRemoveLabel: "Remove {{name}}",
            messageLabel: "Message",
            messagePlaceholder: "Say what they did and why it mattered.",
            messageCharactersLeft: "{{count}} characters left",
            audienceLabel: "Audience",
            directLabel: "Direct",
            directDescription: "Only the recipients, their managers, and higher-ups can see this.",
            publicLabel: "Public",
            publicDescription: "Everyone at the company can see this.",
            fromLabel: "From {{name}}",
            cancelLabel: "Cancel",
            submitDirectLabel: "Send directly",
            submitPublicLabel: "Send publicly",
            discardChangesTitle: "Discard this Good Vibe?",
            discardChangesBody: "You have not sent this yet. Leaving now will lose what you have written.",
            discardChangesConfirmLabel: "Discard",
            discardChangesCancelLabel: "Keep editing",
            successMessage: "You just made someone's day!",
            successCloseLabel: "Close",
            mostUsedThisMonthLabel: "Most used this month",
            usageCountLabel: "{{count}} uses this month"
        },
        giveError: "Couldn't send. Please try again.",
        visibility: {
            public: "Public",
            direct: "Direct"
        },
        sealed: {
            ariaLabel: "New Good Vibes for {{recipients}}. Press and hold to open.",
            badge: "New Good Vibes",
            pressAndHold: "Press and hold to open"
        }
    },
    GoodVibesToolbar: {
        audience: {
            label: "Audience",
            triggerPrefix: "Audience:",
            clearAriaLabel: "Clear audience filter",
            options: {
                all: "All",
                public: "Public",
                direct: "Direct"
            }
        },
        direction: {
            groupLabel: "Direction",
            triggerPrefix: "Direction:",
            clearAriaLabel: "Clear direction filter",
            options: {
                all: "All",
                received: "Received",
                given: "Sent"
            }
        },
        themes: {
            label: "Themes",
            ariaLabel: "Filter by theme",
            clearFilter: "Clear filter"
        },
        timeframe: {
            ariaLabel: "Time frame",
            clearAriaLabel: "Clear time frame filter",
            options: {
                "7d": "Last 7 days",
                "30d": "Last 30 days",
                "3m": "Last 3 months",
                "6m": "Last 6 months",
                quarter: "This quarter",
                year: "This year",
                all: "All time"
            }
        },
        resetFilters: "Reset filters",
        sendInClassic: "Send Good Vibes",
        sendGoodVibes: "Send Good Vibes"
    },
    GoodVibesCard: { ...goodVibeCardEn },
    GoodVibesDetailDialog: { ...goodVibeDetailDialogEn },
    TeamGoodVibesWidget: {
        title: "Good Vibes",
        viewAll: "View all",
        viewAllAriaLabel: "View all Good Vibes",
        topRecipients: "Top recipients",
        noRecipients: "No recognition in the last 30 days.",
        recognitionCount_one: "{{count}}",
        recognitionCount_other: "{{count}}",
        summary: "Last 30 days · {{count}} of {{total}} recognized"
    },
    TeamFeedbackPage: {
        error: "We couldn't load team feedback right now.",
        empty: {
            headline: "No feedback waiting.",
            subtext: "New messages from your team will land here."
        },
        filteredEmpty: {
            headline: "No feedback matches these filters",
            subtext: "Try clearing your filters or search."
        },
        segmentUnsupported: {
            title: "Advanced filtering isn't supported here yet",
            description: "Feedback doesn't support advanced filtering yet. Switch to My team or Everyone to see this tab."
        },
        audienceRefusal: {
            segmentNotVisible: {
                title: "This segment isn't available to you",
                description: "You no longer have access to this segment. Pick another one or clear the filter to see feedback."
            },
            handPickedSegment: {
                title: "Hand-picked segments aren't supported here",
                description: "Feedback can only be scoped to rule-based segments. Pick a rule-based segment or clear the filter."
            },
            brokenRules: {
                title: "This segment has rules we can't read",
                description: "Some of this segment's rules aren't supported, so feedback can't be scoped to it. Edit the segment or clear the filter."
            },
            unsupportedCapability: {
                title: "This segment isn't supported here yet",
                description: "Feedback can't be scoped to a segment that matches any rule. Switch it to match all rules instead."
            }
        },
        liveRegion: {
            loading: "Loading team feedback",
            loadingMoreAnnouncement: "Loading more feedback",
            loadedMore_one: "Loaded {{count}} more feedback",
            loadedMore_other: "Loaded {{count}} more feedback",
            results_one: "{{count}} result",
            results_other: "{{count}} results"
        },
        ariaLabel: "Team feedback",
        loadingMore: "Loading more",
        unread: "Unread",
        moreCollapsed_one: "+{{count}} more",
        moreCollapsed_other: "+{{count}} more",
        toMonitor: "To monitor",
        toMonitorTooltip: "Feedback to monitor triggered by {{category}}",
        status: {
            awaiting: "Awaiting response"
        },
        totalCount_one: "{{count}} Feedback",
        totalCount_other: "{{count}} Feedback",
        sender: {
            anonymous: "Anonymous"
        },
        typeFilter: {
            label: "Feedback type",
            menuAriaLabel: "Filter by feedback type",
            clearFilter: "Clear feedback type filter",
            dismissAriaLabel: "Remove feedback type filter",
            triggerLabel: "Feedback type · {{count}}",
            triggerLabelSingle: "Feedback type: {{label}}"
        }
    },
    FeedbackToolbar: {
        searchPlaceholder: "Search feedback",
        searchAriaLabel: "Search feedback",
        filters: {
            unreplied: "All unreplied",
            unread: "All unread",
            all: "All feedback",
            ariaLabel: "Filter feedback",
            clearAriaLabel: "Clear feedback filter"
        },
        timeFrame: {
            ariaLabel: "Time frame",
            clearAriaLabel: "Clear time frame filter",
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year"
        },
        addFilter: {
            label: "Add filter",
            menuAriaLabel: "Toggle visible filters"
        },
        resetFilters: "Reset filters",
        fullscreen: {
            expand: "Expand fullscreen",
            collapse: "Close fullscreen"
        }
    },
    SummarizeFeedbackButton: {
        button: "Summarize all feedback",
        pending: "Gathering feedback…",
        error: "Unable to summarize feedback. Please try again.",
        promptReportingLine: "Summarize the feedback from my reports",
        promptDirectReports: "Summarize the feedback from my direct reports",
        promptOrganization: "Summarize the feedback from my organization"
    },
    TeamFeedbackDetail: {
        error: "We couldn't load this feedback right now.",
        liveRegion: {
            loading: "Loading feedback"
        },
        toMonitor: "To monitor",
        toMonitorTooltip: "Feedback to monitor triggered by {{category}}",
        toMonitorBanner: {
            label: "Flagged to monitor",
            hide: "Hide",
            why: "Why?",
            accuracyPrompt: "Was this flag accurate?",
            accurate: "Mark this flag as accurate",
            inaccurate: "Mark this flag as inaccurate",
            thanks: "Thanks for the feedback.",
            reasonDialog: {
                title: "What did we get wrong?",
                description: "Your note helps tune when feedback gets flagged to monitor. Optional.",
                placeholder: "This flag was off because...",
                skip: "Skip",
                submit: "Submit feedback"
            }
        },
        removeToMonitorFlag: {
            button: "Remove to-monitor flag",
            dialog: {
                title: "Remove flag?",
                description: "The to-monitor flag will be removed from this feedback. This can't be undone.",
                cancel: "Cancel",
                confirm: "Remove flag"
            },
            success: "To-monitor flag removed.",
            error: "We couldn't remove the to-monitor flag. Please try again."
        },
        status: {
            awaiting: "Awaiting response"
        },
        sender: {
            anonymous: "Anonymous"
        },
        closestReportingManager: {
            from: "from {{name}}'s team",
            unavailable: "(Manager unavailable)"
        },
        questionSenderLabel: "Workleap",
        details: {
            header: "Details",
            sentiment: "Sentiment",
            metric: "Metric",
            status: "Status",
            feedbackType: "Feedback type",
            topics: "Topics",
            collapse: "Collapse details",
            expand: "Show details"
        },
        emptyReadingPane: "Select a message on the left to read it and reply. Authors stay anonymous unless they choose to sign their message."
    },
    FeedbackReplyComposer: {
        textareaLabel: "Reply to feedback",
        placeholder: "Reply…",
        send: "Send reply",
        sending: "Sending reply…",
        successToast: "Reply sent",
        errorToast: "Couldn't send your reply. Please try again.",
        helpMeReply: "Help me reply",
        undo: "Undo",
        generating: "Reading the conversation…",
        generatingLiveRegion: "Generating a reply draft",
        generateErrorToast: "A reply suggestion couldn't be generated. Please try again.",
        improve: "Improve",
        improveLessFormal: "Less formal",
        improveMoreConcise: "More concise",
        improveMoreNeutral: "More neutral",
        improveMoreSimple: "More simple",
        improveMoreActionable: "More actionable",
        improveMoreAppreciative: "More appreciative",
        improveStartOver: "Start over"
    },
    ManageReviewCyclesLink: {
        label: "Manage all review cycles in Workleap Admin"
    },
    TeamReviewsPage: {
        error: "We couldn't load team reviews right now.",
        noResults: "No reviews were found",
        noResultsHint: "Try adjusting your filters.",
        empty: {
            heading: "No reviews yet",
            hint: "Performance reviews for your team will appear here once a review cycle is underway."
        },
        totalCount_one: "{{count}} Review",
        totalCount_other: "{{count}} Reviews",
        liveRegion: {
            loading: "Loading reviews",
            loadingMoreAnnouncement: "Loading more reviews",
            loadedMore_one: "Loaded {{count}} more result",
            loadedMore_other: "Loaded {{count}} more results",
            results_one: "{{count}} result",
            results_other: "{{count}} results"
        }
    },
    ReviewsToolbar: {
        searchPlaceholder: "Search reviewees",
        searchAriaLabel: "Search reviewees",
        scope: {
            ariaLabel: "Time scope",
            latest: "Latest review",
            past90: "Past 90 days",
            pastYear: "Past year",
            allTime: "All time"
        },
        cycleStatus: {
            ariaLabel: "Cycle status",
            all: "From all cycles",
            active: "From active cycles",
            locked: "From locked cycles"
        },
        sharing: {
            ariaLabel: "Sharing",
            all: "All sharing",
            notShared: "Not shared",
            shared: "Shared",
            acknowledged: "Acknowledged"
        },
        clearScope: "Clear time scope filter",
        clearCycleStatus: "Clear cycle status filter",
        clearSharing: "Clear sharing filter",
        resetFilters: "Reset filters"
    },
    ReviewsTable: {
        ariaLabel: "Team reviews",
        columns: {
            review: "Review",
            score: "Score",
            period: "Period",
            types: "Types",
            sharing: "Sharing"
        },
        expandRow: "Show reviewers for {{reviewee}}",
        collapseRow: "Hide reviewers for {{reviewee}}",
        drawerRow: "Reviewers for {{reviewee}}",
        noReviewsGroup: "No reviews ({{count}})"
    },
    ReviewRow: {
        noReviewsYet: "No reviews yet",
        mobileLabels: {
            score: "Review score",
            period: "Evaluation period",
            types: "Review types",
            sharing: "Sharing"
        },
        types: {
            self: "Self",
            manager: "Manager",
            peer: "Peer",
            upward: "Upward"
        },
        sharing: {
            notShared: "Not shared",
            shared: "Shared",
            acknowledged: "Acknowledged"
        }
    },
    ReviewLotDrawer: {
        liveRegion: {
            loading: "Loading reviewers"
        },
        error: "Couldn't load reviewers.",
        retry: "Retry",
        empty: "No reviewers for this review."
    },
    LotReviewerRow: {
        roles: {
            self: "Self",
            manager: "Manager",
            peer: "Peer",
            upward: "Upward"
        },
        status: {
            notStarted: "Not started",
            inProgress: "In progress",
            submitted: "Submitted",
            overdue: "Overdue"
        },
        submittedOn: "on {{date}}",
        dueOn: "Due {{date}}",
        anonymous: {
            peer: "Anonymous peer",
            upward: "Anonymous upward reviewer",
            reviewer: "Anonymous reviewer"
        },
        noDirectManager: "No Direct Manager",
        deletedMember: "Deleted member"
    },
    TeamMeetingsPage: {
        notConnectedBody: "Connect your calendar so the agent can see availability, schedule your recurring 1:1s, and draft a fact-based agenda before each meeting.",
        connectCalendar: "Connect a calendar",
        expiredBody: "Your calendar connection has expired. Reconnect it and the agent can go back to scheduling your 1:1s and drafting an agenda before each meeting.",
        reconnectCalendar: "Reconnect a calendar",
        organizeMeetings: "Schedule meetings",
        error: "We couldn't load your meetings right now.",
        noDataTitle: "No meetings scheduled yet",
        noData: "Your calendar is connected, but no recurring 1:1s are booked yet. Organize your meetings and the agent will schedule them, then draft a fact-based agenda before each one.",
        noResults: "No meetings match your filters.",
        clearFilters: "Clear filters",
        totalCount_one: "{{count}} Meeting",
        totalCount_other: "{{count}} Meetings",
        liveRegion: {
            sortChanged: "Sorted by date {{direction}}",
            ascending: "ascending",
            descending: "descending",
            loading: "Loading meetings",
            loadingMoreAnnouncement: "Loading more meetings",
            loadedMore_one: "{{count}} more meeting loaded",
            loadedMore_other: "{{count}} more meetings loaded",
            results_one: "{{count}} meeting",
            results_other: "{{count}} meetings"
        }
    },
    TeamGoalsPage: {
        create: {
            newGoal: "New goal",
            error: "Error creating goal"
        },
        error: "We couldn't load team goals right now.",
        empty: {
            heading: "No goals yet",
            hint: "Team goals will appear here once someone creates one."
        },
        noResults: "No goals were found",
        noResultsHint: "Try adjusting your filters.",
        totalCount_one: "{{count}} Goal",
        totalCount_other: "{{count}} Goals",
        tableAriaLabel: "Team goals",
        columns: {
            title: "Goal",
            dueDate: "Due",
            status: "Status",
            progress: "Progress",
            lastUpdatedAt: "Updated"
        },
        liveRegion: {
            loading: "Loading goals",
            loadingMoreAnnouncement: "Loading more goals",
            loadedMore_one: "Loaded {{count}} more goal",
            loadedMore_other: "Loaded {{count}} more goals",
            results_one: "{{count}} result",
            results_other: "{{count}} results",
            sortChanged: "Sorted by {{column}} {{direction}}",
            ascending: "ascending",
            descending: "descending"
        }
    },
    TeamGoalDetailPage: {
        editGoal: "Edit goal",
        draft: "Draft",
        archived: "Archived",
        liveRegion: {
            loading: "Loading goal"
        },
        type: {
            organization: "Organization goal",
            team: "Team goal",
            personal: "Individual goal"
        },
        sidebar: {
            owner: "Owner",
            deletedMember: "Deleted member",
            unknownTeam: "Unknown team",
            entireOrganization: "Entire organization",
            teamKind: "Team",
            directReportsOf: "{{name}}'s direct reports",
            reportingLineOf: "{{name}}'s reporting line",
            createdBy: "Created by {{name}}",
            contributors_one: "Contributor ({{count}})",
            contributors_other: "Contributors ({{count}})",
            showAllContributors_one: "Show all contributors ({{count}})",
            showAllContributors_other: "Show all contributors ({{count}})",
            showLessContributors: "Show less",
            parentGoal: "Parent goal",
            startDate: "Start date",
            due: "Due",
            visibility: "Visibility"
        },
        parentScope: {
            organization: "Organization",
            team: "Team",
            personal: "Individual"
        },
        due: {
            dueIn_one: "in {{count}} day",
            dueIn_other: "in {{count}} days",
            today: "Due today",
            overdue: "Overdue"
        },
        visibility: {
            allOrganization: "Visible to the organization",
            ownerAndContributorsOnly: "Visible to the owner and contributors only"
        },
        description: {
            heading: "Description"
        },
        progress: {
            heading: "Progress",
            percent: "{{value}}%",
            ariaLabel: "{{title}} progress"
        },
        status: {
            onTrack: "On track",
            atRisk: "At risk",
            offTrack: "Off track",
            noStatus: "No status"
        },
        trackingMethod: {
            simple: "Simple progress — a single percentage, updated manually",
            measurements: "Metrics — weighted average of the measurements below",
            subGoals: "Sub-goals — updated manually, based on the sub-goals below",
            subGoalsAutomatic: "Auto-update from sub-goals — recalculated whenever a sub-goal changes"
        },
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            goals: "Goals"
        }
    },
    TeamGoalNotFound: {
        heading: "Goal not found",
        body: "This goal doesn't exist or you don't have access to it.",
        backToGoals: "Back to goals"
    },
    GoalMeasurementRow: {
        valuePair: "{{current}} → {{target}}",
        boolean: {
            completed: "Completed",
            incomplete: "Incomplete"
        },
        progressAriaLabel: "{{title}} progress",
        progressPercent: "{{value}}%"
    },
    GoalUpdates: {
        heading: "Updates",
        noUpdates: "No updates posted yet.",
        showAll_one: "Show all updates ({{count}})",
        showAll_other: "Show all updates ({{count}})",
        showLess: "Show less"
    },
    GoalUpdateCard: {
        relativeTime: {
            today: "today",
            days_one: "{{count}} day ago",
            days_other: "{{count}} days ago"
        },
        overallProgress: "Overall progress",
        progressTransition: "{{previous}}% → {{current}}%",
        progressDelta: "({{delta}}%)",
        progressValue: "{{value}}%",
        measurementTransition: "{{previous}} → {{current}}",
        status: {
            onTrack: "On track",
            atRisk: "At risk",
            offTrack: "Off track",
            noStatus: "No status"
        }
    },
    GoalProgressUpdate: {
        addUpdate: "Update progress",
        actions: {
            ariaLabel: "Actions for {{author}}'s update",
            automaticAuthor: "this automatic",
            edit: "Edit",
            delete: "Delete"
        },
        status: {
            onTrack: "On track",
            atRisk: "At risk",
            offTrack: "Off track",
            noStatus: "No status"
        },
        addedToast: "Progress update added",
        addErrorToast: "Error adding progress update",
        editedToast: "Progress updated successfully",
        editErrorToast: "Error updating progress",
        deletedToast: "Progress update deleted",
        deleteErrorToast: "Sorry, something went wrong. Please try again.",
        dialog: {
            addTitle: "Update progress",
            editTitle: "Edit progress",
            statusLabel: "Status",
            progressLabel: "Goal progress",
            progressPercent: "{{value}}%",
            progressInvalid: "Enter a number between 0 and 100.",
            progressFromMeasurements: "Calculated from the measurements above. Type over it to set it yourself.",
            progressFromSubgoals: "Auto-update from sub-goals is enabled. Progress cannot be manually updated.",
            loadingAriaLabel: "Loading the goal",
            measurementsLabel: "Measurements",
            measurementTarget: "Target: {{target}}",
            measurementInvalid: "Enter a number.",
            commentLabel: "Comment",
            commentPlaceholder: "Add a comment about this progress update...",
            cancel: "Cancel",
            addSubmit: "Save progress",
            editSubmit: "Save changes"
        },
        delete: {
            title: "Delete progress update?",
            body: "This progress update will be deleted. This action cannot be undone.",
            confirm: "Delete",
            cancel: "Cancel"
        },
        discard: {
            title: "Discard this update?",
            body: "You have not saved this yet. Leaving now will lose what you have written.",
            confirm: "Discard",
            cancel: "Keep editing"
        }
    },
    GoalSubgoals: {
        deletedMember: "Deleted member",
        heading_one: "Subgoals ({{count}})",
        heading_other: "Subgoals ({{count}})",
        type: {
            personal: "Individual goal",
            team: "Team goal",
            organization: "Organization goal"
        },
        status: {
            onTrack: "On track",
            atRisk: "At risk",
            offTrack: "Off track",
            noStatus: "No status"
        },
        progressAriaLabel: "{{title}} progress",
        progressPercent: "{{value}}%",
        dueLabel: "Due {{date}}",
        latestUpdate: {
            heading: "Latest update",
            noUpdates: "No updates posted yet.",
            today: "today",
            days_one: "{{count}} day ago",
            days_other: "{{count}} days ago",
            weeks_one: "{{count}} week ago",
            weeks_other: "{{count}} weeks ago"
        }
    },
    TeamReviewWritePage: {
        untitledCycle: "Review",
        breadcrumb: { ariaLabel: "Breadcrumb", team: "Team", reviews: "Reviews", reviewType: { manager: "Manager review", self: "Self review", peer: "Peer review", upward: "Upward review" } },
        cycleDescription: { expand: "Show more", collapse: "Show less" },
        perspectives: { yours: "Your review", manager: "Manager", self: "Self", peer: "Peers", upward: "Upward" },
        assistant: {
            welcomeMessage: "Hi {{reviewerName}}, let's get going on {{revieweeName}}'s review",
            welcomeMessageSelf: "Hi {{reviewerName}}, let's get going on your review",
            subtext: {
                role: "currently {{jobTitle}}",
                team: "{{name}} is on the {{department}} team",
                teamTenure_one: "{{name}} has been at {{company}} for {{count}} year, on the {{department}} team",
                teamTenure_other: "{{name}} has been at {{company}} for {{count}} years, on the {{department}} team",
                teamTenureLessThanYear: "{{name}} has been at {{company}} for less than a year, on the {{department}} team",
                tenure_one: "{{name}} has been at {{company}} for {{count}} year",
                tenure_other: "{{name}} has been at {{company}} for {{count}} years",
                tenureLessThanYear: "{{name}} has been at {{company}} for less than a year",
                selfTenure_one: "{{count}} year at {{company}}",
                selfTenure_other: "{{count}} years at {{company}}",
                selfTenureLessThanYear: "Less than a year at {{company}}"
            },
            inputPlaceholder: "Ask me to prepare or draft an answer…",
            initialSuggestion: "Help me prepare the review"
        },
        loading: "Loading review",
        progress: "{{answered}} of {{total}} answered",
        autoSave: { saving: "Saving…", saved: "Saved", error: "Couldn't save — retrying" },
        submit: "Submit review",
        submitBlocked: "Answer every required question to submit",
        submitSucceeded: "Review submitted",
        submitFailed: "Couldn't submit the review",
        submitBlockedUnsaved: "We couldn't save your latest answer, so the review wasn't submitted. We're still retrying — try again in a moment.",
        cancel: "Cancel",
        update: "Update review",
        updateSucceeded: "Review updated",
        unsavedChanges: "You have unsaved changes",
        saveFailed: "We couldn't save your changes. They're still here — try again.",
        assistantUpdated: "Workleap AI updated this review",
        discard: {
            heading: "Leave without saving?",
            body: "Your changes to this review haven't been saved. Leaving now discards them.",
            keepEditing: "Keep editing",
            discard: "Discard changes"
        },
        showAnswersInline: "Show answers inline",
        question: {
            optional: "Optional",
            textPlaceholder: "Write your answer…",
            ratingLabel: "Rating",
            choiceLabel: "Choices",
            unlabeledValue: "Level {{value}}",
            inlineSelectedBy: "Also selected by {{names}}",
            notInYourReview: "Not part of your review"
        },
        context: {
            title: "Context",
            open: "Show context",
            close: "Hide context",
            workleapGroup: "Workleap",
            externalGroup: "External",
            emptyGroup: "Nothing here yet",
            progress: "{{value}}%",
            showing: "Showing {{shown}} of {{total}}",
            viewAll: "View all ({{count}})",
            back: "Back",
            sourcesCount: "{{count}} sources",
            sourceFrom: "from {{source}}",
            sourceTypes: {
                Role: "Role",
                Goals: "Goals",
                CurrentReviews: "Current reviews",
                PastReviews: "Past reviews",
                Recognition: "Good Vibes",
                Agendas: "1on1s",
                Engagement: "Engagement",
                Feedback: "Feedback"
            },
            visibility: {
                public: "Public",
                direct: "Direct"
            }
        }
    },
    ReviewDetailPage: {
        liveRegion: {
            loading: "Loading review"
        },
        empty: {
            heading: "This review hasn't started yet",
            hint: "Answers will appear here once the review is underway."
        },
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            team: "Team",
            reviews: "Reviews"
        }
    },
    ReviewDetailHeader: {
        untitledCycle: "Performance review",
        covers: "Covers {{period}}",
        visibility: {
            reportingline: "Visible to {{name}}'s direct manager and higher-ups"
        },
        acknowledgedBy: "Acknowledged by {{name}} · {{date}}",
        sharedBy: "Shared by {{name}} · {{date}}"
    },
    ReviewSharePreview: {
        pageTitle: "Preview shared review",
        visibilityListHeader: "This is the view that will be shared with {{name}}. This includes:",
        types: {
            manager: "Manager answers they have access to",
            self: "Self-review answers",
            peerAnonymous: "Peer answers they have access to (anonymized)",
            peerWithNames: "Peer answers they have access to (with names visible)",
            upwardAnonymous: "Upward review answers they have access to (anonymized)",
            upwardWithNames: "Upward review answers they have access to (with names visible)"
        },
        editsVisible: "Any edits made after sharing will also be visible.",
        acknowledgmentWarning: "This cycle requires acknowledgment, so you won't be able to unshare once shared.",
        cancel: "Cancel",
        share: "Share"
    },
    ReviewDetailActions: {
        actions: "Review actions",
        preparing: "Preparing…",
        manage: "Manage review"
    },
    ReviewExportAction: {
        exportPdf: "Export PDF",
        preparing: "Preparing…",
        fullVersion: "Full version",
        fullVersionDescription: "Every question and answer on this review.",
        revieweeVersion: "Reviewee version",
        revieweeVersionDescription: "Only what the reviewee can see. Questions hidden from them are left out.",
        successToast: "PDF file was exported",
        errorToast: "The export failed. Please try again."
    },
    ReviewReleaseActions: {
        previewAndShare: "Preview and share",
        editReview: "Edit review",
        reviewType: {
            manager: "Manager review",
            self: "Self review",
            peer: "Peer review",
            upward: "Upward review"
        },
        shareDisabledReason: "This review can't be shared because all questions are hidden from the reviewee.",
        unshare: "Unshare",
        acknowledge: "Acknowledge",
        cancel: "Cancel",
        unshareDialog: {
            title: "Unshare this review?",
            description: "{{name}} will lose access to any manager and peer feedback for this review until it's shared again.",
            confirm: "Unshare review"
        },
        acknowledgeDialog: {
            title: "Acknowledge this review?",
            description: "Acknowledging confirms that you've read this review. Your name and the date will be recorded on the review as proof of receipt.",
            confirmation: "I confirm I've read this review",
            confirm: "Acknowledge"
        },
        shareSuccessToast: "You shared this review",
        unshareSuccessToast: "You unshared this review",
        errorToast: "Sorry, something went wrong. Please try again."
    },
    ParticipantCard: {
        kind: {
            reviewee: "Reviewee",
            manager: "Manager",
            peer: "Peer",
            upward: "Upward"
        },
        anonymousName: {
            peer: "Anonymous peer",
            upward: "Anonymous upward reviewer"
        },
        managerRole: {
            direct: "Direct Manager",
            temporary: "temporary Direct Manager",
            previous: "previous Direct Manager"
        },
        status: {
            notstarted: "Not started",
            inprogress: "In progress",
            submitted: "Submitted"
        },
        submittedOn: "on {{date}}",
        dueOn: "Due {{date}}",
        deletedMember: "Deleted member",
        noDirectManager: "No Direct Manager"
    },
    ReviewQuestionCard: {
        badges: {
            noAnswer: "No answer",
            optional: "Optional"
        },
        emptyAnswer: {
            text: "No answer yet.",
            rating: "Not rated yet.",
            choice: "No selection yet."
        },
        reviewerKind: {
            manager: "Manager review",
            self: "Self-review",
            peer: "Peer review",
            upward: "Upward review"
        },
        anonymousReviewer: "Anonymous reviewer",
        deletedMember: "Deleted member",
        ratingUnlabeledValue: "Level {{value}}",
        offScaleAnswers: "Answers outside the current scale",
        selectedBy: "Selected by {{names}}"
    },
    ReviewDetailNotFound: {
        heading: "Review not found",
        body: "This review doesn't exist, or you don't have access to it.",
        backToReviews: "Back to reviews"
    },
    ReviewSummary: {
        eyebrow: "AI summary",
        disclaimer: "This summary was generated by AI and may contain inaccuracies. Review it alongside the full responses below.",
        errorToast: "Something went wrong. Please try again.",
        callout: {
            heading: "Need a quick overview?",
            body: "Generate an AI summary to see the main themes from all submitted reviews in one place.",
            generate: "Generate summary",
            generating: "Generating summary…",
            noTextQuestions: "A summary can't be generated because this review cycle has no text-based questions."
        },
        menu: {
            triggerAriaLabel: "Summary actions",
            regenerate: "Regenerate",
            dismiss: "Dismiss"
        },
        liveRegion: {
            loading: "Loading review summary…"
        }
    },
    EngagementMetricDetailPage: {
        liveRegion: {
            loading: "Loading engagement metric details…"
        },
        questionsTableAriaLabel: "{{metric}} questions",
        timeFrame: {
            relativeLabel: {
                "7d": "7 days",
                "30d": "30 days",
                "3m": "3 months",
                "6m": "6 months",
                quarter: "this quarter",
                year: "this year",
                allTime: "all time"
            }
        }
    },
    EngagementMetricDetailHeader: {
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            engagement: "Back to Engagement"
        },
        tooltipLabel: "About {{metric}}"
    },
    EngagementMetricDetailToolbar: {
        timeFrame: {
            ariaLabel: "Time frame",
            "7d": "Last 7 days",
            "30d": "Last 30 days",
            "3m": "Last 3 months",
            "6m": "Last 6 months",
            quarter: "This quarter",
            year: "This year",
            allTime: "All time"
        }
    },
    ScopeSegmentsSection: {
        searchPlaceholder: "Search saved segments",
        header: "Saved segments",
        error: "Couldn't load segments.",
        retry: "Try again",
        loadMore: "Loading more segments",
        empty: "No saved segments yet.",
        noResults: "No segments match your search.",
        star: "Star {{name}}",
        unstar: "Unstar {{name}}",
        starError: "Couldn't update this segment. Try again.",
        addFilter: "Add filter",
        manageSegments: "Manage segments",
        liveRegion: {
            loadingSegments: "Loading saved segments",
            loadedMore_one: "Loaded {{count}} more segment",
            loadedMore_other: "Loaded {{count}} more segments",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    EngagementMetricDetailNotFound: {
        heading: "Metric not found",
        body: "This engagement metric doesn't exist or you don't have access to it.",
        backToEngagement: "Back to Engagement"
    },
    TeamFilterPopover: {
        trigger: "Add filter",
        dialogTitle: "Apply filters",
        loading: "Loading employee fields",
        error: "Couldn't load your employee fields.",
        retry: "Try again",
        matchCount_one: "{{count}} person matches",
        matchCount_other: "{{count}} people match",
        countLoading: "Checking who matches",
        matchTypeFixed: "Matching any rule isn't available here.",
        matchAnySegment: "This segment matches any of its rules, so it can't be edited here. It still applies exactly as it was saved.",
        saveAsSegment: "Save as segment",
        applyFilters: "Apply filters"
    },
    TeamFilterActions: {
        appliedRulesLabel: "Applied filters",
        saveAsSegment: "Save as segment",
        clearFilters: "Clear filters"
    },
    SaveAsSegmentDialog: {
        title: "Save as segment",
        back: "Back to filters",
        name: {
            label: "Name",
            placeholder: "Engineering in Toronto",
            required: "Enter a name for this segment.",
            tooLong: "Keep the name under {{count}} characters."
        },
        save: "Save segment",
        cancel: "Cancel",
        createdToast: "Segment created",
        saveErrorToast: "Couldn't save this segment. Try again."
    },
    ManageSegmentsModal: {
        title: "Manage segments"
    },
    ManageSegmentsSidebar: {
        heading: "Segments",
        searchPlaceholder: "Find a segment",
        listAriaLabel: "Segments",
        newSegment: "New segment",
        mySegments: "My segments",
        sharedWithMe: "Shared with me",
        sharedBy: "Shared by {{name}}",
        applied: "Applied",
        currentFilters: "Current filters",
        currentFiltersCaption_one: "{{count}} rule",
        currentFiltersCaption_other: "{{count}} rules",
        empty: "No segments yet.",
        noResults: "No segments match your search.",
        loadError: "Couldn't load segments.",
        retry: "Try again",
        loadMore: "Loading more segments",
        liveRegion: {
            loading: "Loading segments",
            loadedMore_one: "Loaded {{count}} more segment",
            loadedMore_other: "Loaded {{count}} more segments",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    ManageSegmentsEditorPanel: {
        emptyTitle: "No segment selected",
        emptyDescription: "Select a segment to edit, or create a new one.",
        loading: "Loading segment",
        loadError: "Couldn't load this segment.",
        retry: "Try again",
        notFoundTitle: "This segment doesn't exist, or it isn't shared with you.",
        backToList: "Back to segments"
    },
    TeamSegmentForm: {
        moreActionsAriaLabel: "More segment actions",
        sharedPill: "Shared",
        matchTypeFixed: "Matching any rule isn't available here.",
        apply: "Apply",
        saveAsSegment: "Save as segment",
        saveAsNew: "Save as new",
        copyName: "{{name}} copy",
        saveChanges: "Save changes",
        deleteConfirm: {
            title: "Delete “{{name}}”?",
            description: "This removes the segment for everyone it's shared with. The people in it and their data are not affected. This can't be undone.",
            cancel: "Cancel",
            confirm: "Delete"
        }
    },
    CurrentFiltersForm: {
        title: "Current filters",
        matchTypeFixed: "Matching any rule isn't available here.",
        saveAsSegment: "Save as segment",
        apply: "Apply"
    }
};
